import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { getRaceOperatorBatches, getRaceOperatorOverview, getOperatorTasks } from '@/apis/operator';
import useLocaleStore, { LocaleTypeEnum } from '@/store/locale';

const useOperatorStore = defineStore('operator', () => {
    const localeStore = useLocaleStore();

    // 批次列表
    const batches = ref<any[]>([]);

    // 批次下拉列表（查询条件使用）
    const batchOptions = computed(() =>
        batches.value.map((item: any) => ({
            label: localeStore.currentLocale === LocaleTypeEnum.English ? `Batch ${item.batch_no}` : `第${item.batch_no}批`,
            value: item.batch_no,
        }))
    );

    // 批次列表查询接口
    const fetchBatches = async (rid: string | number) => {
        try {
            const res = await getRaceOperatorBatches(rid);
            if (res?.data) {
                batches.value = res.data;
            }
        } catch (e) {
            console.error('获取批次赛程失败:', e);
        }
    };

    // 当前批次
    const currentBatch = ref<any>(null);

    // 赛题地图视图：map 全景地图 / batch 批次赛题地图
    const mapView = ref<'map' | 'batch'>('map');

    // 当前批次号
    const currentBatchNo = computed(() => {
        const no = currentBatch.value?.batch_no;
        if (!no && no !== 0) return '';
        return String(no);
    });

    // 赛道统计数据
    const trackStats = ref<any>(null);

    // 芯片目录（当前适配的所有芯片）
    const gpuCatalog = ref<any>(null);

    // 芯片列表（按当前语言显示名称，后缀以接口返回为准）
    const gpuList = computed(() =>
        Object.entries(gpuCatalog.value || {}).map(([key, item]: any) => ({
            key,
            name: localeStore.currentLocale === LocaleTypeEnum.English ? item.en : item.cn,
            suffix: item.suffix ?? '',
            filename: item.filename,
        }))
    );

    // 赛道概况查询接口
    const fetchOverview = async (rid: string | number) => {
        try {
            const res = await getRaceOperatorOverview(rid);
            if (res?.data) {
                if (res.data.stats) {
                    trackStats.value = res.data.stats;
                }
                if (res.data.current_batch) {
                    currentBatch.value = res.data.current_batch;
                }
                if (res.data.gpu_catalog) {
                    gpuCatalog.value = res.data.gpu_catalog;
                }
            }
        } catch (e) {
            console.error('获取赛道概况失败:', e);
        }
    };

    // 提交成功后的刷新信号：递增后各模块（如 rightInfo 参赛概况）重新拉取数据
    const participationVersion = ref(0);

    const refreshParticipation = () => {
        participationVersion.value += 1;
    };

    // 赛题列表
    const operatorTasks = ref<any[]>([]);

    // 赛题发布计划（含未开放赛题的 expected_release_at）
    const taskReleaseSchedule = ref<any[]>([]);

    // 赛题列表查询接口
    const fetchTasks = async (rid: string | number, params?: any) => {
        try {
            const res = await getOperatorTasks(rid, params);
            if (res?.data) {
                operatorTasks.value = res.data;
            }
            if (Array.isArray(res?.task_release_schedule)) {
                taskReleaseSchedule.value = res.task_release_schedule;
            }
        } catch (e) {
            console.error('获取赛题列表失败:', e);
        }
    };

    return {
        batches,
        batchOptions,
        fetchBatches,
        currentBatch,
        currentBatchNo,
        trackStats,
        gpuCatalog,
        gpuList,
        fetchOverview,
        operatorTasks,
        taskReleaseSchedule,
        fetchTasks,
        mapView,
        participationVersion,
        refreshParticipation,
    };
});

export default useOperatorStore;
